import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  CalendarDaysIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  DocumentTextIcon,
  UserIcon,
  BellIcon,
  MapPinIcon,
  TagIcon,
  CogIcon,
  ShieldCheckIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';

const MaintenanceScheduler = ({ isNewMaintenance = false, maintenance: propMaintenance = null }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // calendar, list, upcoming
  const [showTaskForm, setShowTaskForm] = useState(isNewMaintenance);
  const [editingTask, setEditingTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Sample mock maintenance data
  const mockMaintenanceTasks = [
    {
      id: 'M001',
      equipmentId: 'EQP003',
      equipmentName: 'Banc de musculation ajustable',
      type: 'preventive',
      scheduledDate: '2023-08-15',
      scheduledTime: '09:00',
      description: 'Vérification des fixations et lubrification',
      technician: 'Ahmed Technicien',
      estimatedDuration: '45',
      priority: 'medium',
      status: 'scheduled',
      cost: '5000',
      parts: ['Vis de fixation', 'Lubrifiant'],
      checklist: ['Vérifier les fixations', 'Tester la stabilité', 'Lubrifier les articulations']
    },
    {
      id: 'M002',
      equipmentId: 'EQP001',
      equipmentName: 'Tapis de course professionnel',
      type: 'corrective',
      scheduledDate: '2023-08-10',
      scheduledTime: '14:00',
      description: 'Remplacement de la courroie usée',
      technician: 'Karim Maintenance',
      estimatedDuration: '120',
      priority: 'high',
      status: 'scheduled',
      cost: '12000',
      parts: ['Courroie de transmission', 'Lubrifiant pour tapis'],
      checklist: ['Démonter l\'ancien tapis', 'Installer la nouvelle courroie', 'Tester le fonctionnement']
    },
    {
      id: 'M003',
      equipmentId: 'EQP002',
      equipmentName: 'Vélo elliptique',
      type: 'inspection',
      scheduledDate: '2023-08-05',
      scheduledTime: '10:30',
      description: 'Inspection trimestrielle régulière',
      technician: 'Samira Technicienne',
      estimatedDuration: '30',
      priority: 'low',
      status: 'completed',
      cost: '2500',
      parts: [],
      checklist: ['Vérifier l\'électronique', 'Tester la résistance', 'Vérifier le système de freinage']
    },
    {
      id: 'M004',
      equipmentId: 'EQP005',
      equipmentName: 'Presse à cuisse',
      type: 'preventive',
      scheduledDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      scheduledTime: '11:00',
      description: 'Maintenance préventive mensuelle',
      technician: 'Youcef Technicien',
      estimatedDuration: '60',
      priority: 'medium',
      status: 'scheduled',
      cost: '3500',
      parts: ['Graisse mécanique'],
      checklist: ['Vérifier les câbles', 'Lubrifier les poulies', 'Contrôler les poids']
    },
    {
      id: 'M005',
      equipmentId: 'EQP007',
      equipmentName: 'Développé couché',
      type: 'corrective',
      scheduledDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      scheduledTime: '09:30',
      description: 'Réparation du système de sécurité',
      technician: 'Karim Maintenance',
      estimatedDuration: '90',
      priority: 'critical',
      status: 'scheduled',
      cost: '7500',
      parts: ['Mécanisme de sécurité', 'Boulons de fixation'],
      checklist: ['Remplacer le système défectueux', 'Tester sous charge', 'Vérifier la stabilité']
    }
  ];

  // Fetch maintenance data
  const { data: maintenanceData, isLoading } = useQuery('maintenance', async () => {
    // For demo purposes, use mock data
    // In production, this would be:
    // const response = await axios.get('/equipments/maintenance');
    // return response.data;
    
    // Return mock data instead
    return { maintenance: mockMaintenanceTasks };
  });

  // Fetch equipment data for form
  const { data: equipmentData } = useQuery('equipment', async () => {
    const response = await axios.get('/equipments');
    return response.data;
  });

  // Utiliser les données des props si disponibles, sinon utiliser les données de la requête
  const maintenance = propMaintenance || maintenanceData?.maintenance || [];
  const equipment = equipmentData?.equipment || [];

  // Maintenance task form state
  const [taskForm, setTaskForm] = useState({
    equipmentId: '',
    equipmentName: '',
    type: 'preventive',
    scheduledDate: new Date().toISOString().split('T')[0],
    scheduledTime: '10:00',
    description: '',
    technician: '',
    estimatedDuration: '60',
    priority: 'medium',
    notes: '',
    status: 'scheduled',
    cost: '',
    parts: [''],
    checklist: [''],
  });

  // Reset form
  const resetForm = () => {
    setTaskForm({
      equipmentId: '',
      equipmentName: '',
      type: 'preventive',
      scheduledDate: new Date().toISOString().split('T')[0],
      scheduledTime: '10:00',
      description: '',
      technician: '',
      estimatedDuration: '60',
      priority: 'medium',
      notes: '',
      status: 'scheduled',
      cost: '',
      parts: [''],
      checklist: [''],
    });
  };

  // Create maintenance task mutation
  const createTaskMutation = useMutation(
    (taskData) => axios.post('/equipments/maintenance', taskData),
    {
      onSuccess: () => {
        toast.success('Tâche de maintenance créée avec succès!');
        queryClient.invalidateQueries('maintenance');
        setShowTaskForm(false);
        resetForm();
        if (isNewMaintenance) {
          navigate('/equipment');
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la création');
      },
    }
  );

  // Update maintenance task mutation
  const updateTaskMutation = useMutation(
    ({ id, ...taskData }) => axios.put(`/equipments/maintenance/${id}`, taskData),
    {
      onSuccess: () => {
        toast.success('Tâche de maintenance mise à jour avec succès!');
        queryClient.invalidateQueries('maintenance');
        setShowTaskForm(false);
        setEditingTask(null);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
      },
    }
  );

  // Delete maintenance task mutation
  const deleteTaskMutation = useMutation(
    (taskId) => axios.delete(`/equipments/maintenance/${taskId}`),
    {
      onSuccess: () => {
        toast.success('Tâche de maintenance supprimée avec succès!');
        queryClient.invalidateQueries('maintenance');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
      },
    }
  );

  // Mark task as completed mutation
  const completeTaskMutation = useMutation(
    ({ id, completionData }) => 
      axios.patch(`/equipments/maintenance/${id}/complete`, completionData),
    {
      onSuccess: () => {
        toast.success('Tâche marquée comme terminée!');
        queryClient.invalidateQueries('maintenance');
        queryClient.invalidateQueries('equipment');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la completion');
      },
    }
  );

  // Fill form when editing
  useEffect(() => {
    if (editingTask) {
      setTaskForm({
        ...editingTask,
        scheduledDate: editingTask.scheduledDate ? new Date(editingTask.scheduledDate).toISOString().split('T')[0] : '',
        scheduledTime: editingTask.scheduledTime || '',
        parts: editingTask.parts || [''],
        checklist: editingTask.checklist || [''],
      });
    }
  }, [editingTask]);

  // Montrer automatiquement le formulaire si on est en mode création
  useEffect(() => {
    if (isNewMaintenance) {
      setShowTaskForm(true);
    }
  }, [isNewMaintenance]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    if (!taskForm.equipmentId || !taskForm.scheduledDate || !taskForm.description) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }

    const cleanedData = {
      ...taskForm,
      parts: taskForm.parts.filter(part => part.trim() !== ''),
      checklist: taskForm.checklist.filter(item => item.trim() !== ''),
      cost: parseFloat(taskForm.cost) || 0,
      estimatedDuration: parseInt(taskForm.estimatedDuration) || 60,
    };

    if (editingTask) {
      updateTaskMutation.mutate({ id: editingTask.id, ...cleanedData });
    } else {
      createTaskMutation.mutate(cleanedData);
    }
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...taskForm[field]];
    newArray[index] = value;
    setTaskForm(prev => ({ ...prev, [field]: newArray }));
  };

  const addArrayField = (field) => {
    setTaskForm(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeArrayField = (field, index) => {
    if (taskForm[field].length > 1) {
      const newArray = taskForm[field].filter((_, i) => i !== index);
      setTaskForm(prev => ({ ...prev, [field]: newArray }));
    }
  };

  const handleCompleteTask = (task) => {
    const completionData = {
      completedAt: new Date().toISOString(),
      completedBy: 'Current User', // Replace with actual user
      actualCost: task.cost || 0,
      actualDuration: task.estimatedDuration || 60,
      completionNotes: '',
    };
    
    completeTaskMutation.mutate({ id: task.id, completionData });
  };

  // Filter maintenance tasks
  const filteredMaintenance = maintenance.filter(task => {
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesType = filterType === 'all' || task.type === filterType;
    return matchesStatus && matchesType;
  });

  // Get tasks for selected date
  const getTasksForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return maintenance.filter(task => 
      task.scheduledDate && task.scheduledDate.startsWith(dateStr)
    );
  };

  // Get upcoming tasks (next 7 days)
  const getUpcomingTasks = () => {
    const now = new Date();
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return maintenance.filter(task => {
      const taskDate = new Date(task.scheduledDate);
      return taskDate >= now && taskDate <= nextWeek && task.status === 'scheduled';
    }).sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  };

  // Get overdue tasks
  const getOverdueTasks = () => {
    const now = new Date();
    return maintenance.filter(task => {
      const taskDate = new Date(task.scheduledDate);
      return taskDate < now && task.status === 'scheduled';
    });
  };

  const maintenanceTypes = [
    { id: 'preventive', name: 'Préventive', icon: ClockIcon, color: 'blue' },
    { id: 'corrective', name: 'Corrective', icon: ExclamationTriangleIcon, color: 'red' },
    { id: 'inspection', name: 'Inspection', icon: DocumentTextIcon, color: 'green' },
    { id: 'calibration', name: 'Calibration', icon: CogIcon, color: 'purple' },
  ];

  const priorityLevels = [
    { id: 'low', name: 'Faible', color: 'green' },
    { id: 'medium', name: 'Moyenne', color: 'yellow' },
    { id: 'high', name: 'Élevée', color: 'orange' },
    { id: 'critical', name: 'Critique', color: 'red' },
  ];

  const upcomingTasks = getUpcomingTasks();
  const overdueTasks = getOverdueTasks();

  // Generate calendar days
  const generateCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfWeek = firstDay.getDay();
    // Total days in month
    const daysInMonth = lastDay.getDate();
    
    // Generate array of day objects
    const days = [];
    
    // Add previous month's days to fill the first week
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    // Add current month's days
    const today = new Date();
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString()
      });
    }
    
    // Fill remaining days from next month
    const daysToAdd = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= daysToAdd; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays(selectedDate);
  
  // Format month name
  const formatMonth = (date) => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };
  
  // Navigate to previous/next month
  const goToPreviousMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setSelectedDate(newDate);
  };
  
  const goToNextMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setSelectedDate(newDate);
  };

  // Get tasks for specific day
  const getTasksForSpecificDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return maintenance.filter(task => 
      task.scheduledDate === dateStr
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Planificateur de Maintenance</h2>
          <p className="text-white/60 mt-1">
            Gérez les tâches de maintenance préventive et corrective
          </p>
        </div>
        <button
          onClick={() => setShowTaskForm(true)}
          className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nouvelle Tâche</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">À venir</p>
              <p className="text-xl font-bold text-white">{upcomingTasks.length}</p>
            </div>
            <ClockIcon className="h-5 w-5 text-blue-400" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">En retard</p>
              <p className="text-xl font-bold text-white">{overdueTasks.length}</p>
            </div>
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Terminées</p>
              <p className="text-xl font-bold text-white">
                {maintenance.filter(t => t.status === 'completed').length}
              </p>
            </div>
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total</p>
              <p className="text-xl font-bold text-white">{maintenance.length}</p>
            </div>
            <WrenchScrewdriverIcon className="h-5 w-5 text-purple-400" />
          </div>
        </div>
      </div>

      {/* View Mode Selector */}
      <div className="flex space-x-4">
        <button
          onClick={() => setViewMode('calendar')}
          className={`px-4 py-2 rounded-xl transition-colors ${
            viewMode === 'calendar'
              ? 'bg-blue-500 text-white'
              : 'bg-white/10 text-white/60 hover:bg-white/20'
          }`}
        >
          Calendrier
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={`px-4 py-2 rounded-xl transition-colors ${
            viewMode === 'list'
              ? 'bg-blue-500 text-white'
              : 'bg-white/10 text-white/60 hover:bg-white/20'
          }`}
        >
          Liste
        </button>
        <button
          onClick={() => setViewMode('upcoming')}
          className={`px-4 py-2 rounded-xl transition-colors ${
            viewMode === 'upcoming'
              ? 'bg-blue-500 text-white'
              : 'bg-white/10 text-white/60 hover:bg-white/20'
          }`}
        >
          À venir
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tous les statuts</option>
          <option value="scheduled">Planifiées</option>
          <option value="in-progress">En cours</option>
          <option value="completed">Terminées</option>
          <option value="cancelled">Annulées</option>
        </select>

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tous les types</option>
          {maintenanceTypes.map(type => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </select>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'upcoming' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Tasks */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-blue-400" />
              Tâches à venir
            </h3>
            <div className="space-y-3">
              {upcomingTasks.length > 0 ? (
                upcomingTasks.map((task) => (
                  <div
                    key={task.id}
                    className="p-4 bg-white/5 rounded-xl border border-white/10"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-white">{task.equipmentName}</h4>
                        <p className="text-white/60 text-sm mt-1">{task.description}</p>
                        <div className="flex items-center space-x-4 mt-2 text-white/50 text-sm">
                          <span>
                            {new Date(task.scheduledDate).toLocaleDateString('fr-FR')}
                          </span>
                          <span>{task.scheduledTime}</span>
                          <span className="capitalize">{task.type}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleCompleteTask(task)}
                          className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                          title="Marquer comme terminée"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingTask(task);
                            setShowTaskForm(true);
                          }}
                          className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                          title="Modifier"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-white/60 text-center py-8">
                  Aucune tâche à venir dans les 7 prochains jours
                </p>
              )}
            </div>
          </div>

          {/* Overdue Tasks */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-red-400" />
              Tâches en retard
            </h3>
            <div className="space-y-3">
              {overdueTasks.length > 0 ? (
                overdueTasks.map((task) => (
                  <div
                    key={task.id}
                    className="p-4 bg-red-500/10 rounded-xl border border-red-500/20"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-white">{task.equipmentName}</h4>
                        <p className="text-white/60 text-sm mt-1">{task.description}</p>
                        <div className="flex items-center space-x-4 mt-2 text-red-400 text-sm">
                          <span>
                            {new Date(task.scheduledDate).toLocaleDateString('fr-FR')}
                          </span>
                          <span>{task.scheduledTime}</span>
                          <span className="capitalize">{task.type}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleCompleteTask(task)}
                          className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                          title="Marquer comme terminée"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingTask(task);
                            setShowTaskForm(true);
                          }}
                          className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                          title="Modifier"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-white/60 text-center py-8">
                  Aucune tâche en retard
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {viewMode === 'list' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="space-y-4">
            {filteredMaintenance.length > 0 ? (
              filteredMaintenance.map((task) => (
                <div
                  key={task.id}
                  className="p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="font-medium text-white">{task.equipmentName}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          task.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                          task.status === 'in-progress' ? 'bg-yellow-500/20 text-yellow-400' :
                          task.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {task.status}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          task.priority === 'low' ? 'bg-green-500/20 text-green-400' :
                          task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          task.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                      <p className="text-white/60 text-sm mt-1">{task.description}</p>
                      <div className="flex items-center space-x-4 mt-2 text-white/50 text-sm">
                        <span>
                          {new Date(task.scheduledDate).toLocaleDateString('fr-FR')}
                        </span>
                        <span>{task.scheduledTime}</span>
                        <span className="capitalize">{task.type}</span>
                        <span>{task.technician}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {task.status === 'scheduled' && (
                        <button
                          onClick={() => handleCompleteTask(task)}
                          className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                          title="Marquer comme terminée"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingTask(task);
                          setShowTaskForm(true);
                        }}
                        className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                        title="Modifier"
                      >
                        <DocumentTextIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteTaskMutation.mutate(task.id)}
                        className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                        title="Supprimer"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-white/60 text-center py-8">
                Aucune tâche de maintenance trouvée
              </p>
            )}
          </div>
        </div>
      )}

      {viewMode === 'calendar' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          {/* Calendar Navigation */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">
              {formatMonth(selectedDate)}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={goToPreviousMonth}
                className="p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
              >
                <ChevronLeftIcon className="h-5 w-5 text-white" />
              </button>
              <button
                onClick={() => setSelectedDate(new Date())}
                className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors text-sm"
              >
                Aujourd'hui
              </button>
              <button
                onClick={goToNextMonth}
                className="p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
              >
                <ChevronRightIcon className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day Names */}
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
              <div key={day} className="text-center text-white/70 font-medium py-2">
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {calendarDays.map((day, i) => {
              const dayTasks = getTasksForSpecificDay(day.date);
              const hasTasks = dayTasks.length > 0;
              const hasOverdueTasks = dayTasks.some(task => 
                new Date(task.scheduledDate) < new Date() && task.status === 'scheduled'
              );
              
              return (
                <div
                  key={i}
                  className={`min-h-[100px] p-2 rounded-lg border ${
                    day.isToday
                      ? 'bg-blue-500/20 border-blue-500/30'
                      : day.isCurrentMonth
                      ? 'bg-white/5 border-white/10 hover:bg-white/10'
                      : 'bg-white/5 border-transparent text-white/30'
                  } transition-colors`}
                >
                  <div className="flex justify-between items-start">
                    <span className={`text-sm font-medium ${day.isToday ? 'text-blue-400' : ''}`}>
                      {day.date.getDate()}
                    </span>
                    {hasTasks && (
                      <span className={`w-2 h-2 rounded-full ${
                        hasOverdueTasks ? 'bg-red-500' : 'bg-green-500'
                      }`}></span>
                    )}
                  </div>

                  {/* Tasks for this day */}
                  <div className="mt-1 space-y-1">
                    {dayTasks.slice(0, 2).map((task) => (
                      <div
                        key={task.id}
                        onClick={() => {
                          setEditingTask(task);
                          setShowTaskForm(true);
                        }}
                        className={`text-xs p-1 rounded truncate cursor-pointer ${
                          task.type === 'preventive'
                            ? 'bg-blue-500/20 text-blue-400'
                            : task.type === 'corrective'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}
                        title={task.description}
                      >
                        {task.equipmentName}
                      </div>
                    ))}
                    {dayTasks.length > 2 && (
                      <div className="text-xs text-white/60 text-center">
                        +{dayTasks.length - 2} plus
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Task Form Modal */}
      {showTaskForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingTask ? 'Modifier la tâche' : 'Nouvelle tâche de maintenance'}
              </h2>
              <button
                onClick={() => {
                  setShowTaskForm(false);
                  setEditingTask(null);
                  resetForm();
                }}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Équipement *
                  </label>
                  <select
                    value={taskForm.equipmentId}
                    onChange={(e) => {
                      const equipment = equipmentData?.equipment?.find(eq => eq.id === e.target.value);
                      setTaskForm(prev => ({
                        ...prev,
                        equipmentId: e.target.value,
                        equipmentName: equipment?.name || '',
                      }));
                    }}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un équipement</option>
                    {equipment.map(eq => (
                      <option key={eq.id} value={eq.id} className="bg-gray-800">
                        {eq.name} - {eq.brand}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Type de maintenance *
                  </label>
                  <select
                    value={taskForm.type}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {maintenanceTypes.map(type => (
                      <option key={type.id} value={type.id} className="bg-gray-800">
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Date programmée *
                  </label>
                  <input
                    type="date"
                    value={taskForm.scheduledDate}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, scheduledDate: e.target.value }))}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Heure programmée
                  </label>
                  <input
                    type="time"
                    value={taskForm.scheduledTime}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, scheduledTime: e.target.value }))}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Technicien
                  </label>
                  <input
                    type="text"
                    value={taskForm.technician}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, technician: e.target.value }))}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom du technicien"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Priorité
                  </label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {priorityLevels.map(priority => (
                      <option key={priority.id} value={priority.id} className="bg-gray-800">
                        {priority.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Durée estimée (minutes)
                  </label>
                  <input
                    type="number"
                    value={taskForm.estimatedDuration}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="15"
                    step="15"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Coût estimé (DA)
                  </label>
                  <input
                    type="number"
                    value={taskForm.cost}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, cost: e.target.value }))}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  Description *
                </label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows="3"
                  placeholder="Description détaillée de la maintenance..."
                  required
                />
              </div>

              {/* Parts */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Pièces nécessaires
                </label>
                <div className="space-y-2">
                  {taskForm.parts.map((part, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={part}
                        onChange={(e) => handleArrayFieldChange('parts', index, e.target.value)}
                        className="flex-1 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: Courroie de transmission"
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayField('parts', index)}
                        className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayField('parts')}
                    className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
                  >
                    + Ajouter une pièce
                  </button>
                </div>
              </div>

              {/* Checklist */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Liste de contrôle
                </label>
                <div className="space-y-2">
                  {taskForm.checklist.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => handleArrayFieldChange('checklist', index, e.target.value)}
                        className="flex-1 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: Vérifier les fixations"
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayField('checklist', index)}
                        className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayField('checklist')}
                    className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
                  >
                    + Ajouter un élément
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  Notes
                </label>
                <textarea
                  value={taskForm.notes}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows="2"
                  placeholder="Notes supplémentaires..."
                />
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    setShowTaskForm(false);
                    setEditingTask(null);
                    resetForm();
                  }}
                  className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createTaskMutation.isLoading || updateTaskMutation.isLoading}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50"
                >
                  {createTaskMutation.isLoading || updateTaskMutation.isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Traitement...</span>
                    </div>
                  ) : (
                    editingTask ? 'Modifier' : 'Créer'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default MaintenanceScheduler; 